<template>
  <TransitionRoot as="template" :show="!!props.open">
    <Dialog as="div" class="relative z-10" @close="close">
      <TransitionChild
        as="template"
        enter="ease-out duration-100"
        enter-from="opacity-0"
        enter-to="opacity-100"
        leave="ease-in duration-200"
        leave-from="opacity-100"
        leave-to="opacity-0"
      >
        <div
          class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
        />
      </TransitionChild>

      <div class="fixed inset-0 z-10">
        <div
          class="flex min-h-full items-end justify-center text-center sm:items-center sm:p-0"
        >
          <TransitionChild
            as="template"
            enter="ease-out duration-300"
            enter-from="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-to="opacity-100 translate-y-0 sm:scale-100"
            leave="ease-in duration-200"
            leave-from="opacity-100 translate-y-0 sm:scale-100"
            leave-to="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
          >
            <DialogPanel
              class="relative w-full transform overflow-hidden rounded-t-2xl bg-white px-4 pt-6 pb-8 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-sm sm:p-6"
            >
              <div class="absolute right-0 top-0 pr-4 pt-4 sm:block">
                <button
                  type="button"
                  class="rounded-sm bg-white text-gray-400 hover:text-gray-500 border-transparent focus:border-transparent focus:ring-0 outline-none"
                  @click="close"
                >
                  <span class="sr-only">Close</span>
                  <XMarkIcon class="h-6 w-6" aria-hidden="true" />
                </button>
              </div>
              <div class="flex flex-col space-y-1 mt-4">
                <template v-for="item in taskFolders">
                  <Link
                    @click="clearFilters"
                    :key="item.name"
                    v-if="!item.hidden"
                    :href="item.href"
                    :class="[
                      isActive(item.href)
                        ? 'bg-gray-50 text-gray-900 hover:text-gray-900 hover:bg-gray-100'
                        : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50',
                      'group flex items-center px-2 py-2 font-medium rounded-sm',
                    ]"
                  >
                    <span class="truncate flex-1 pl-3" v-html="item.name" />
                  </Link>
                </template>
              </div>

              <div
                class="mt-2 flex flex-col space-y-1 border-t border-t-gray-100 pt-4"
              >
                <span
                  v-if="taskViews && taskViews.length > 0"
                  class="uppercase text-gray-900 text-xs font-semibold tracking-wide mb-1"
                  >{{ t("admin.navigation.custom_views.label") }}</span
                >
                <div class="overflow-y-auto h-[400px]">
                  <Link
                    v-for="item in taskViews"
                    @click="clearFilters"
                    :key="item.name"
                    :href="route('tasks.views.show', item.id)"
                    :class="[
                      isActive(route('tasks.views.show', item.id))
                        ? 'bg-gray-50 text-gray-900 hover:text-gray-900 hover:bg-gray-100'
                        : 'text-gray-500 hover:text-gray-900 hover:bg-gray-50',
                      'group flex items-center px-2 py-2 font-medium rounded-sm',
                    ]"
                  >
                    <span class="truncate flex-1 pl-3">{{ item.name }}</span>
                    <Menu as="div" class="relative inline-block text-left">
                      <div>
                        <MenuButton
                          @click.prevent
                          class="flex items-center rounded-full text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-0"
                        >
                          <span class="sr-only">Open options</span>
                          <EllipsisVerticalIcon
                            class="h-6 w-6"
                            aria-hidden="true"
                          />
                        </MenuButton>
                      </div>

                      <transition
                        enter-active-class="transition ease-out duration-100"
                        enter-from-class="transform opacity-0 scale-95"
                        enter-to-class="transform opacity-100 scale-100"
                        leave-active-class="transition ease-in duration-75"
                        leave-from-class="transform opacity-100 scale-100"
                        leave-to-class="transform opacity-0 scale-95"
                      >
                        <MenuItems
                          class="absolute bottom-0 right-0 z-10 mt-2 w-56 origin-top-right rounded-sm bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none"
                        >
                          <div class="py-1">
                            <MenuItem @click.prevent="handleRenameView(item)">
                              <span
                                class="block px-4 py-2 text-base text-gray-700"
                              >
                                Rename
                              </span>
                            </MenuItem>
                            <MenuItem
                              @click.prevent="handleDeleteView(item.id)"
                            >
                              <span
                                class="block px-4 py-2 text-base text-gray-700"
                              >
                                Delete
                              </span>
                            </MenuItem>
                          </div>
                        </MenuItems>
                      </transition>
                    </Menu>
                  </Link>
                </div>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </div>
    </Dialog>
  </TransitionRoot>
</template>

<script setup>
import { computed, defineEmits, defineProps } from "vue";
import {
  Dialog,
  DialogPanel,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  TransitionChild,
  TransitionRoot,
} from "@headlessui/vue";
import { Square3Stack3DIcon } from "@heroicons/vue/24/outline";
import route from "ziggy-js";
import { Link, usePage } from "@inertiajs/vue3";
import { EllipsisVerticalIcon } from "@heroicons/vue/20/solid";
import { useI18n } from "vue-i18n";
import { router } from "@inertiajs/vue3";
import { useEventBus } from "@/Tailwind/Mixins/useEventBus";
import { XMarkIcon } from "@heroicons/vue/24/outline";

const { t } = useI18n();

const emit = defineEmits(["close", "rename", "delete"]);

const props = defineProps({
  open: Boolean,
});

let taskViews = computed(() => usePage().props.auth.tasks_views);

const taskFolders = [
  {
    name: t("admin.navigation.all.label"),
    icon: Square3Stack3DIcon,
    href: route("tasks.index"),
  },
  {
    name:
      t("admin.navigation.overdue.label") +
      '&nbsp;&nbsp;<span class="text-red-600">' +
      usePage().props.tasks_overdue +
      "</span>",
    href: route("tasks.overdue"),
    current: isActive(route("tasks.overdue")),
    hidden: usePage().props.tasks_overdue === 0,
  },
];

function isActive(to) {
  const url = `${location.origin}${location.pathname}`;

  return url === to || url.search(to) !== -1;
}

const close = () => emit("close");

function handleRenameView(view) {
  emit("rename", view);
}

function handleDeleteView(id) {
  router.delete(route("tasks.views.destroy", [id]), {
    onSuccess: () => emit("delete", id),
  });
}

const { emit: emitEventBus } = useEventBus();

const clearFilters = () => {
  emitEventBus("clearFilters");
};
</script>
